import { useCallback, useEffect, useRef, useState } from "react";

// What went wrong, as one of the reasons the studio puts in the visitor's
// words: "blocked", "missing", "busy", "unsupported" or "failed".
const problemFor = (error) => {
  switch (error?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "blocked";
    case "NotFoundError":
    case "OverconstrainedError":
      return "missing";
    case "NotReadableError":
    case "AbortError":
      return "busy";
    case "NotSupportedError":
      return "unsupported";
    default:
      return "failed";
  }
};

const release = (stream) => {
  stream.current?.getTracks().forEach((t) => t.stop());
  stream.current = null;
};

// The visitor's webcam, shown only on this page: the browser asks for
// permission on `start`, nothing is recorded or sent anywhere, and the camera
// turns off on `stop` or when the page section goes away.
//
// status: "off" | "asking" | "on" | "problem"; `problem` is the failure's
// reason (problemFor), for the caller to explain in the page's language.
export default function useCamera() {
  const [status, setStatus] = useState("off");
  const [problem, setProblem] = useState(null);
  const stream = useRef(null);
  const video = useRef(null);
  const live = useRef(true);

  const stop = useCallback(() => {
    release(stream);
    setStatus("off");
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setProblem("unsupported");
      setStatus("problem");
      return;
    }
    setStatus("asking");
    setProblem(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      if (!live.current) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = s;
      // Unplugging the camera or revoking the permission ends the track.
      s.getVideoTracks().forEach((t) => t.addEventListener("ended", () => {
        if (stream.current === s) stop();
      }));
      setStatus("on");
    } catch (error) {
      if (!live.current) return;
      const kind = problemFor(error);
      if (kind === "failed") console.error("Camera preview failed", error);
      setProblem(kind);
      setStatus("problem");
    }
  }, [stop]);

  // The <video> mounts once the camera is on; hand it the stream then.
  useEffect(() => {
    if (status === "on" && video.current) video.current.srcObject = stream.current;
  }, [status]);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      release(stream);
    };
  }, []);

  return { status, problem, start, stop, video };
}
